// Iter 40 — Admin Progress oversight.
//
// Read-only school-wide progress surface. Mirrors the provider-side
// CaseloadProgress experience but:
//   • shows ALL students across providers, not just one provider's caseload
//   • carries the FULL provider list serving each student (not just one)
//   • adds filters: grade · service domain · provider · student · status flag
//   • sorts by needs_attention by default so admin's eye lands on what
//     matters first
//
// Data: GET /api/progress/admin (Iter 40 endpoint)
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { TrendUp, TrendDown, Minus, ArrowRight, Warning, MagnifyingGlass, ChartLineUp, Funnel } from "@phosphor-icons/react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkline } from "@/pages/progress/Sparkline";
import { TREND_META, FLAG_META, fmtDate, daysSince } from "@/pages/progress/utils";
import { formatCanonicalPercent } from "@/lib/goalAverage";
import api from "@/lib/api";
import { useAdminProgress } from "@/lib/adminQueries";
import { RefreshChip } from "@/lib/formatUpdatedAgo";

const FLAG_RANK = {
  declining: 0, no_data: 1, stale_data: 2, plateauing: 3,
  sparse: 4, improving: 5, at_target: 6,
};

const DOMAIN_LABELS = {
  speech_language: "Speech-Language",
  ot: "Occupational Therapy",
  pt: "Physical Therapy",
  social_behavior: "Social-Behavior",
  academics: "Academics",
  hearing_vision: "Hearing & Vision",
  transition: "Transition",
  health_nursing: "Health & Nursing",
  support_access: "Support & Access",
  enrichment_other: "Enrichment & Other",
};

function HeadlineTile({ label, value, accent = "#3F4756", testid, sub, onClick, active = false, disabled = false }) {
  const isButton = typeof onClick === "function";
  const base = "bg-white border rounded-lg p-3.5 flex flex-col gap-0.5 transition-all text-left";
  const state = active
    ? "border-[#3F4756] ring-2 ring-[#3F4756]/15 shadow-sm"
    : isButton && !disabled
      ? "border-[#E5E3DB] hover:border-[#3F4756]/40 hover:shadow-sm cursor-pointer"
      : "border-[#E5E3DB]";
  const Wrapper = isButton ? "button" : "div";
  return (
    <Wrapper
      className={`${base} ${state} ${disabled ? "opacity-60 cursor-default" : ""}`}
      data-testid={testid}
      data-active={active ? "true" : "false"}
      {...(isButton ? { onClick, disabled, type: "button" } : {})}
    >
      <p className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold">{label}</p>
      <p className="text-2xl font-semibold tabular-nums" style={{ color: accent }}>{value}</p>
      {sub && <p className="text-[10px] text-[#9B9890]">{sub}</p>}
    </Wrapper>
  );
}

function StudentRow({ row, onSelect }) {
  const { student, goals, providers, domains, flags, last_data_date, active_goal_count } = row;
  const days = daysSince(last_data_date);
  const topGoals = goals.slice(0, 2);
  return (
    <Card
      data-testid={`admin-progress-row-${student.id}`}
      className="rounded-xl border border-[#E5E3DB] bg-white shadow-sm hover:shadow-md hover:border-[#3F4756]/40 transition-all cursor-pointer"
      onClick={() => onSelect(student.id)}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-full bg-[#EDF2F7] text-[#3F4756] flex items-center justify-center font-semibold heading-font text-sm shrink-0">
            {student.first_name?.[0]}{student.last_name?.[0]}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm font-semibold text-[#1F2937]">{student.first_name} {student.last_name}</p>
              {student.readable_id && (
                <span className="text-[9px] font-mono text-[#9B9890] tabular-nums">{student.readable_id}</span>
              )}
              <span className="text-[11px] text-[#6B7280]">Gr {student.grade}</span>
              <span className="text-[11px] text-[#6B7280]">·</span>
              <span className="text-[11px] text-[#6B7280]">{active_goal_count} active goal{active_goal_count !== 1 ? "s" : ""}</span>
            </div>
            <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
              {(providers || []).map((p) => (
                <span key={p.id} className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-[#F3F1EC] text-[#3F4756]">
                  <span className="font-medium">{p.name}</span>
                  {p.role && <span className="text-[#9B9890]">· {p.role}</span>}
                </span>
              ))}
              {(domains || []).map((d) => (
                <span key={d} className="text-[10px] px-1.5 py-0.5 rounded bg-[#E1EAE4] text-[#1E3F33] font-medium">
                  {DOMAIN_LABELS[d] || d}
                </span>
              ))}
            </div>
            <div className="flex items-center gap-1.5 mt-2 flex-wrap">
              {flags.length === 0 ? (
                <span className="text-[10px] text-[#6B7280]">No flags</span>
              ) : (
                flags.slice(0, 4).map((f) => {
                  const m = FLAG_META[f] || { label: f, text: "#6B7280", bg: "#F3F1EC" };
                  return (
                    <span key={f} data-testid={`admin-flag-${f}`} className="text-[9px] px-1.5 py-0.5 rounded font-medium" style={{ color: m.text, backgroundColor: m.bg }}>
                      {m.label}
                    </span>
                  );
                })
              )}
            </div>
            {topGoals.length > 0 && (
              <div className="mt-3 space-y-1">
                {topGoals.map((g) => {
                  const trend = g.computed_trend?.status || "no_data";
                  const tm = TREND_META[trend] || TREND_META.no_data;
                  const Icon = trend === "improving" ? TrendUp : trend === "declining" ? TrendDown : Minus;
                  // FP3-130A — canonical Goal Average (mean of per-
                  // session percentages) replaces the stale
                  // `current_mastery` snapshot. "—" when no data.
                  const avgDisplay = formatCanonicalPercent(g.canonical_average);
                  return (
                    <div key={g.id} className="flex items-center gap-2 text-[11px]">
                      <Icon size={12} weight="fill" style={{ color: tm.dot }} />
                      <span
                        className="text-[#4B5563] flex-1 truncate"
                        title={
                          g.canonical_average === null || g.canonical_average === undefined
                            ? "No data yet"
                            : `Average across ${g.canonical_session_count || 0} targeted session${(g.canonical_session_count || 0) === 1 ? "" : "s"}`
                        }
                      >
                        {g.category || "Goal"} · {avgDisplay} / {g.target_mastery || 80}%
                      </span>
                      <Sparkline values={(g.computed_trend?.points || []).map((p) => p.value)} />
                      {g.provider_name && (
                        <span className="text-[9px] text-[#9B9890]">{g.provider_name}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            <p className="text-[10px] text-[#9B9890] mt-2.5">
              Last data: {fmtDate(last_data_date)}{days != null ? ` · ${days}d ago` : ""}
            </p>
          </div>
          <ArrowRight size={14} className="text-[#9B9890] shrink-0 mt-1" />
        </div>
      </CardContent>
    </Card>
  );
}

export default function AdminProgress() {
  // FP3-119B — data/loading now driven by React Query (`progressQuery`
  // below). Kept computed here as `data` / `loading` for readability.
  const [providersList, setProvidersList] = useState([]);
  const [filters, setFilters] = useState({ grade: "all", domain: "all", provider_id: "all", flag: "all", search: "" });
  const navigate = useNavigate();
  // FP3-053 — Super-admin context narrowing (URL-driven).
  const [searchParams] = useSearchParams();
  const asOrg = (searchParams.get("as_org") || "").trim();
  const asSchool = (searchParams.get("as_school") || "").trim();

  useEffect(() => {
    api.getProviders?.()?.then((r) => setProvidersList(r.data || []))
      .catch(() => {});
  }, []);

  // FP3-119B — Steady-state cache: /progress/admin renders from
  // React Query cache immediately on revisit; refetches only when
  // stale (3 min) or when a mutation invalidates.
  const progressParams = {
    ...(filters.grade !== "all" ? { grade: filters.grade } : {}),
    ...(filters.domain !== "all" ? { domain: filters.domain } : {}),
    ...(filters.provider_id !== "all" ? { provider_id: filters.provider_id } : {}),
    ...(asOrg ? { as_org: asOrg } : {}),
    ...(asSchool ? { as_school: asSchool } : {}),
  };
  const progressQuery = useAdminProgress(progressParams);
  const data = progressQuery.data || null;
  const loading = progressQuery.isPending;
  const refetching = progressQuery.isFetching && !progressQuery.isPending;
  const dataUpdatedAt = progressQuery.dataUpdatedAt;

  const filtered = useMemo(() => {
    if (!data) return [];
    let rows = data.students;
    const search = filters.search.trim().toLowerCase();
    if (search) {
      rows = rows.filter((r) => {
        const n = `${r.student.first_name} ${r.student.last_name}`.toLowerCase();
        const rid = (r.student.readable_id || "").toLowerCase();
        return n.includes(search) || rid.includes(search);
      });
    }
    if (filters.flag !== "all") {
      // Iter 41 — Composite flag filters for headline-tile drill-ins:
      //   • "needs_attention" = declining OR plateauing OR stale_data OR no_data
      //   • "stale_or_no_data" = stale_data OR no_data
      // Base flags keep their literal match.
      if (filters.flag === "needs_attention") {
        const NA = ["declining", "plateauing", "stale_data", "no_data"];
        rows = rows.filter((r) => (r.flags || []).some((f) => NA.includes(f)));
      } else if (filters.flag === "stale_or_no_data") {
        rows = rows.filter((r) => (r.flags || []).some((f) => ["stale_data", "no_data"].includes(f)));
      } else {
        rows = rows.filter((r) => (r.flags || []).includes(filters.flag));
      }
    }
    // Sort by worst-first
    return [...rows].sort((a, b) => {
      const aw = Math.min(...(a.flags || []).map((f) => FLAG_RANK[f] ?? 99), 99);
      const bw = Math.min(...(b.flags || []).map((f) => FLAG_RANK[f] ?? 99), 99);
      if (aw !== bw) return aw - bw;
      return a.student.last_name.localeCompare(b.student.last_name);
    });
  }, [data, filters.search, filters.flag]);

  const grades = useMemo(() => {
    if (!data) return [];
    return Array.from(new Set(data.students.map((r) => r.student.grade))).sort();
  }, [data]);
  const domains = useMemo(() => {
    if (!data) return [];
    const all = new Set();
    data.students.forEach((r) => (r.domains || []).forEach((d) => all.add(d)));
    return Array.from(all).sort();
  }, [data]);

  const aggregate = data?.aggregate || {};

  return (
    <div className="px-6 py-6 max-w-[1400px] mx-auto" data-testid="admin-progress-page">
      <header className="mb-5">
        <div className="flex items-center justify-between gap-2 mb-1">
          <div className="flex items-center gap-2">
            <ChartLineUp size={20} className="text-[#3F4756]" weight="duotone" />
            <h1 className="text-xl font-semibold text-[#1F2937] heading-font tracking-tight">Progress · Admin</h1>
          </div>
          <RefreshChip
            dataUpdatedAt={dataUpdatedAt}
            isFetching={refetching}
            onRefresh={() => progressQuery.refetch()}
            testid="progress-refresh"
          />
        </div>
        <p className="text-[12px] text-[#6B7280]">
          School-wide goal progress oversight · {aggregate.total_students || 0} students · {aggregate.total_active_goals || 0} active goals
        </p>
      </header>

      {/* Iter 41 — Clickable headline tiles as quick flag filters.
          Each tile sets the `flag` filter; clicking an already-active
          tile clears it. "Total students" resets every filter. */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5" data-testid="admin-progress-headline">
        <HeadlineTile
          testid="admin-progress-needs-attention"
          label="Needs attention"
          value={aggregate.needs_attention_count ?? 0}
          accent="#B85C5C"
          sub="declining · plateauing · stale"
          active={filters.flag === "needs_attention"}
          onClick={() => setFilters((f) => ({ ...f, flag: f.flag === "needs_attention" ? "all" : "needs_attention" }))}
        />
        <HeadlineTile
          testid="admin-progress-improving"
          label="Improving"
          value={aggregate.improving_count ?? 0}
          accent="#1E5A3F"
          active={filters.flag === "improving"}
          onClick={() => setFilters((f) => ({ ...f, flag: f.flag === "improving" ? "all" : "improving" }))}
        />
        <HeadlineTile
          testid="admin-progress-plateauing"
          label="Plateauing"
          value={aggregate.plateauing_count ?? 0}
          accent="#9C6B2E"
          active={filters.flag === "plateauing"}
          onClick={() => setFilters((f) => ({ ...f, flag: f.flag === "plateauing" ? "all" : "plateauing" }))}
        />
        <HeadlineTile
          testid="admin-progress-stale"
          label="Stale / no data"
          value={aggregate.stale_count ?? 0}
          accent="#7D6340"
          sub="≥ 14 days since data"
          active={filters.flag === "stale_or_no_data"}
          onClick={() => setFilters((f) => ({ ...f, flag: f.flag === "stale_or_no_data" ? "all" : "stale_or_no_data" }))}
        />
        <HeadlineTile
          testid="admin-progress-total"
          label="Total students"
          value={aggregate.total_students ?? 0}
          sub={`${aggregate.total_active_goals || 0} active goals · click to reset`}
          active={false}
          onClick={() => setFilters({ grade: "all", domain: "all", provider_id: "all", flag: "all", search: "" })}
        />
      </div>

      {/* Filter bar */}
      <Card className="border-[#E5E3DB] mb-4">
        <CardContent className="p-3">
          <div className="flex flex-wrap items-center gap-2.5">
            <div className="flex items-center gap-1.5 mr-1">
              <Funnel size={13} className="text-[#6B7280]" weight="duotone" />
              <span className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold">Filters</span>
            </div>
            <div className="relative">
              <MagnifyingGlass size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#9B9890]" />
              <Input
                data-testid="admin-progress-search"
                value={filters.search}
                onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
                placeholder="Student or FP-…"
                className="h-8 text-xs pl-7 w-44"
              />
            </div>
            <Select value={filters.grade} onValueChange={(v) => setFilters((f) => ({ ...f, grade: v }))}>
              <SelectTrigger data-testid="admin-progress-grade" className="h-8 text-xs w-28"><SelectValue placeholder="Grade" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All grades</SelectItem>
                {grades.map((g) => <SelectItem key={g} value={g}>Grade {g}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.domain} onValueChange={(v) => setFilters((f) => ({ ...f, domain: v }))}>
              <SelectTrigger data-testid="admin-progress-domain" className="h-8 text-xs w-44"><SelectValue placeholder="Domain" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All domains</SelectItem>
                {domains.map((d) => <SelectItem key={d} value={d}>{DOMAIN_LABELS[d] || d}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.provider_id} onValueChange={(v) => setFilters((f) => ({ ...f, provider_id: v }))}>
              <SelectTrigger data-testid="admin-progress-provider" className="h-8 text-xs w-48"><SelectValue placeholder="Provider" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All providers</SelectItem>
                {(providersList || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.first_name} {p.last_name} · {p.role}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.flag} onValueChange={(v) => setFilters((f) => ({ ...f, flag: v }))}>
              <SelectTrigger data-testid="admin-progress-flag" className="h-8 text-xs w-40"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="needs_attention">Needs attention</SelectItem>
                <SelectItem value="improving">Improving</SelectItem>
                <SelectItem value="plateauing">Plateauing</SelectItem>
                <SelectItem value="stale_or_no_data">Stale / no data</SelectItem>
                <SelectItem value="declining">Declining only</SelectItem>
                <SelectItem value="stale_data">Stale data only</SelectItem>
                <SelectItem value="no_data">No data only</SelectItem>
                <SelectItem value="at_target">At target</SelectItem>
              </SelectContent>
            </Select>
            {(filters.grade !== "all" || filters.domain !== "all" || filters.provider_id !== "all" || filters.flag !== "all" || filters.search) && (
              <button
                data-testid="admin-progress-clear"
                onClick={() => setFilters({ grade: "all", domain: "all", provider_id: "all", flag: "all", search: "" })}
                className="text-[11px] text-[#3F4756] hover:text-[#1E3F33] underline ml-1"
              >
                Clear
              </button>
            )}
            <span className="text-[11px] text-[#9B9890] ml-auto tabular-nums">
              Showing {filtered.length} of {data?.students?.length || 0}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Rows */}
      <div className="space-y-2.5">
        {loading && Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-32 rounded-xl" />
        ))}
        {!loading && filtered.length === 0 && (
          <p className="text-center text-[#6B7280] py-12 text-sm">No students match the current filters.</p>
        )}
        {!loading && filtered.map((row) => (
          <StudentRow key={row.student.id} row={row} onSelect={(id) => navigate(`/admin/students/${id}`)} />
        ))}
      </div>

      <p className="text-[10px] text-[#9B9890] italic mt-6">
        Read-only oversight · provider-level edits live on the provider Progress view (`/progress`).
      </p>
    </div>
  );
}
